import {
  BaseComboboxInputProps,
  ComboboxInput,
} from "@/lib/Components/Form/Inputs/ComboboxInput";
import {
  SupplierOfficeListItem,
  supplierOfficeListQuery,
} from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { ResultOf, VariablesOf } from "@graphql-typed-document-node/core";
import { Link } from "@/components/catalyst/link";
import { supplierOfficeRecordQuery } from "@/app/Offices/GraphQL/supplierOfficeRecordQuery";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { OfficeCreateDialog } from "@/app/Offices/Components/OfficeCreateDialog";
import { useTranslations } from "use-intl";
import { MapPinIcon } from "@heroicons/react/20/solid";
import { CreateSupplierOfficeInput } from "@/gql/graphql";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";

export function SupplierOfficeInput({
  initialFixtureValues,
  supplierId,
  ...props
}: BaseComboboxInputProps<
  ResultOf<typeof supplierOfficeListQuery>,
  VariablesOf<typeof supplierOfficeListQuery>,
  SupplierOfficeListItem
> & {
  initialFixtureValues?: Partial<CreateSupplierOfficeInput>;
  supplierId?: string;
}) {
  const { openAsPromise } = useDialog(OfficeCreateDialog);
  const { supplierId: globalSupplierId } = useGlobalSupplier();
  const resolvedSupplierId = supplierId ?? globalSupplierId ?? undefined;

  return (
    <ComboboxInput
      document={supplierOfficeListQuery}
      accessor={(data) => data.supplierOffices.data}
      LeadingIcon={MapPinIcon}
      getQueryVariables={(search) => ({
        first: 20,
        page: 1,
        search,
        archived: false,
        supplier_id: resolvedSupplierId,
      })}
      recordDocument={supplierOfficeRecordQuery}
      recordAccessor={(data) => data.supplierOffice}
      getViewNode={(item) =>
        item ? (
          <Link
            className="text-blue-500"
            to="."
            search={(prev: any) => ({
              ...prev,
              officeId: item.id,
            })}
          >
            {item.name}
          </Link>
        ) : null
      }
      getKey={(i) => i.id}
      getListItemNode={(i) => <Node model={i} />}
      getInputNode={(i) => i.name}
      fixture={(search) => {
        return {
          label: `Add ${search}`,
          onClick: () => {
            return openAsPromise({
              initialValues: {
                name: search,
                ...initialFixtureValues,
              },
            });
          },
        };
      }}
      {...props}
    />
  );
}

function Node({ model }: { model: SupplierOfficeListItem }) {
  const t = useTranslations("city");
  // The office name is free text, so the city it is actually pinned to has to
  // be visible here — that is what drives pricing, routing and the map.
  const location = [
    model.city?.name,
    model.city?.state ? t(`state.${model.city.state}`) : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div>
      <p>{model?.name}</p>
      <p className="text-xs text-gray-500">{location}</p>
    </div>
  );
}
